<template>
    <BasePageHeader class="m-0" :title="t('myDevelopment.title')" tabindex="-1" data-home-heading />
    <BaseCard v-if="!focusedGoals.length" as="section" padding="none" class="home-welcome">
        <div class="home-welcome-copy">
            <h2 class="display-small-serif whitespace-pre-line text-ink">{{ t("myDevelopment.welcomeTitle") }}</h2>
            <p class="body-small text-ink-muted">{{ welcomeDescription }}</p>
            <BaseButton theme="primary" class="mt-4" :disabled="busy" @click="emit('newGoal')">
                {{ createGoalLabel }}
            </BaseButton>
        </div>
        <div class="home-welcome-animation"><HomeHourglassAnimation /></div>
    </BaseCard>
    <BaseCard v-else as="section" padding="none" :aria-label="t('myDevelopment.focusedGoals')">
        <header class="home-group-header">
            <div>
                <h2 class="body-serif text-ink">{{ t("myDevelopment.focusedGoals") }}</h2>
                <p class="scaffold-body-small text-ink-muted">{{ weekLabel }}</p>
            </div>
            <BaseButton theme="primary" size="sm" class="shrink-0" :disabled="busy" @click="emit('ongoing', focusedGoals.length === 1 ? focusedGoals[0] : undefined)">
                {{ t("myDevelopment.chatWithNadia") }}
            </BaseButton>
        </header>
        <article v-for="goal in focusedGoals" :key="goal.id" :data-goal-id="goal.id" class="home-focused-goal home-goal-row">
            <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <h3 class="body-small-semibold text-ink">{{ goal.title }}</h3>
                    <p class="scaffold-body-small text-ink-muted">
                        {{ t("myDevelopment.actionsComplete", { completed: goal.completed_action_count, total: goal.committed_action_count }) }}
                        <template v-if="goal.target_date">{{ t("myDevelopment.dueSuffix", { date: formatDate(goal.target_date, "month-day") }) }}</template>
                    </p>
                </div>
                <div class="home-row-actions" :data-open="openMenuGoalId === goal.id">
                    <BaseDropdownMenu :sections="goalMenu(goal)" @update:open="(open) => setOpenMenu(goal.id, open)" @select="(item) => selectGoalAction(item, goal)">
                        <template #trigger>
                            <BaseButton theme="neutral" size="sm" icon-only :disabled="busy" :aria-label="t('myDevelopment.goalOptions', { title: goal.title })">
                                <i class="bi bi-three-dots" aria-hidden="true" />
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                </div>
            </div>
            <ul v-if="goal.week_actions.length" class="home-action-list">
                <li v-for="action in goal.week_actions" :key="action.id">{{ action.title }}</li>
            </ul>
            <p v-else class="body-small text-ink-muted mt-2">{{ t("myDevelopment.noWeekActions") }}</p>
        </article>
    </BaseCard>
    <section v-if="otherGoals.length || focusedGoals.length" :aria-label="t(focusedGoals.length ? 'myDevelopment.otherGoals' : 'myDevelopment.existingGoals')">
        <h2 v-if="!focusedGoals.length" class="body-small-semibold mb-4 border-b border-border pb-3 text-ink">{{ t("myDevelopment.existingGoals") }}</h2>
        <BaseCard padding="none">
            <header v-if="focusedGoals.length" class="home-group-header">
                <h2 class="body-serif text-ink">{{ t("myDevelopment.otherGoals") }}</h2>
                <BaseButton theme="neutral" size="sm" :disabled="busy" @click="emit('newGoal')">
                    {{ t("myDevelopment.newGoal") }}
                </BaseButton>
            </header>
            <article
                v-for="goal in otherGoals"
                :key="goal.id"
                :data-goal-id="goal.id"
                class="home-saved-goal home-goal-row flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0">
                    <h3 class="body-small-semibold text-ink">{{ goal.title }}</h3>
                    <p class="scaffold-details-small text-ink-muted mt-1">
                        {{ t("myDevelopment.setOn", { date: formatDate(goal.created_at, "month-day") }) }}
                    </p>
                </div>
                <div class="home-row-actions flex items-center gap-3 self-end sm:self-auto" :data-open="openMenuGoalId === goal.id">
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        class="whitespace-nowrap"
                        :disabled="busy"
                        :aria-label="t('myDevelopment.buildPlanFor', { title: goal.title })"
                        @click="emit('plan', goal)">
                        {{ t("myDevelopment.buildPlan") }}
                    </BaseButton>
                    <BaseDropdownMenu :sections="goalMenu(goal)" @update:open="(open) => setOpenMenu(goal.id, open)" @select="(item) => selectGoalAction(item, goal)">
                        <template #trigger>
                            <BaseButton theme="neutral" size="sm" icon-only :disabled="busy" :aria-label="t('myDevelopment.goalOptions', { title: goal.title })">
                                <i class="bi bi-three-dots" aria-hidden="true" />
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                </div>
            </article>
            <p v-if="!otherGoals.length" class="body-small text-ink-muted p-6">{{ t("myDevelopment.noOtherGoals") }}</p>
        </BaseCard>
    </section>
    <BaseCard v-if="data.unlinked_week_actions.length" as="section" padding="none" :aria-label="t('myDevelopment.otherActions')">
        <header class="home-group-header">
            <div>
                <h2 class="body-serif text-ink">{{ t("myDevelopment.otherActions") }}</h2>
                <p class="scaffold-body-small text-ink-muted">{{ weekLabel }}</p>
            </div>
        </header>
        <ul class="home-action-list m-6">
            <li v-for="action in data.unlinked_week_actions" :key="action.id">{{ action.title }}</li>
        </ul>
    </BaseCard>
</template>

<script setup lang="ts">
import type { MyDevelopmentGoal, MyDevelopmentHome } from "~vue/api/generated/my-development";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import HomeHourglassAnimation from "~vue/pages/myDevelopment/HomeHourglassAnimation.vue";
import { formatDate, formatDateRange } from "~vue/utils/formatDate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ data: MyDevelopmentHome; busy?: boolean }>();
const emit = defineEmits<{
    focus: [goal: MyDevelopmentGoal];
    delete: [goal: MyDevelopmentGoal];
    plan: [goal: MyDevelopmentGoal];
    newGoal: [];
    ongoing: [goal?: MyDevelopmentGoal];
}>();
const { t } = useI18n();
const activeGoals = computed(() => props.data.goals.filter((goal) => goal.status === "in_progress"));
const focusedGoals = computed(() => activeGoals.value.filter((goal) => goal.is_focused));
const otherGoals = computed(() => activeGoals.value.filter((goal) => !goal.is_focused));
const weekLabel = computed(() => t("myDevelopment.thisWeek", { dates: formatDateRange(props.data.week_start, props.data.week_end, "month-day", { year: "never" }) }));
const welcomeDescription = computed(() => (activeGoals.value.length ? t("myDevelopment.chooseDescription") : t("myDevelopment.welcomeDescription")));
const createGoalLabel = computed(() => (activeGoals.value.length ? t("myDevelopment.createGoal") : t("myDevelopment.setGoal")));
const openMenuGoalId = ref<string | null>(null);
function setOpenMenu(goalId: string, open: boolean | undefined) {
    if (open) {
        openMenuGoalId.value = goalId;
    } else if (openMenuGoalId.value === goalId) {
        openMenuGoalId.value = null;
    }
}
function goalMenu(goal: MyDevelopmentGoal): DropdownMenuSection[] {
    return [
        {
            id: "goal",
            items: [
                { id: "focus", type: "button", label: goal.is_focused ? t("myDevelopment.removeFocus") : t("myDevelopment.makeFocus"), disabled: props.busy },
                { id: "delete", type: "button", label: t("myDevelopment.deleteGoal"), disabled: props.busy },
            ],
        },
    ];
}
function selectGoalAction(item: DropdownMenuItem, goal: MyDevelopmentGoal) {
    if (item.id === "focus") {
        emit("focus", goal);
    } else if (item.id === "delete") {
        emit("delete", goal);
    }
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";
@layer components {
    .home-welcome {
        container-type: inline-size;
        @apply flex items-center justify-between gap-10 p-6 md:p-10;
    }
    .home-welcome-copy {
        @apply flex min-w-0 flex-1 flex-col items-start gap-1;
    }
    .home-welcome-animation {
        @apply hidden min-w-0 max-w-75 flex-1;
    }
    @container (min-width: 40rem) {
        .home-welcome-copy {
            @apply w-90 flex-none;
        }
        .home-welcome-animation {
            @apply flex;
        }
    }
    .home-group-header {
        @apply flex items-center justify-between gap-4 border-b border-border p-6;
    }
    .home-focused-goal {
        @apply border-b border-border px-6 py-5 last:border-b-0;
    }
    .home-saved-goal {
        @apply border-b border-border px-6 py-5 last:border-b-0;
    }
    .home-action-list {
        @apply mt-3 flex list-disc flex-col gap-2 pl-6 body-small text-ink marker:text-border-strong;
    }
    .home-row-actions {
        @apply shrink-0;
    }
    @media (min-width: 48rem) and (hover: hover) and (pointer: fine) {
        .home-row-actions {
            @apply opacity-0;
        }
        .home-goal-row:hover .home-row-actions,
        .home-goal-row:focus-within .home-row-actions,
        .home-row-actions[data-open="true"] {
            @apply opacity-100;
        }
    }
}
</style>
